// Global packages and components
import { clsx } from 'clsx'
import { Children, cloneElement, isValidElement, useRef } from 'react'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'

// Styles
import '@splidejs/react-splide/css/core'

// Types
import type { ReactNode, RefObject } from 'react'
import type { Options, Splide as SplideType } from '@splidejs/splide'

interface CarouselProps {
  children: ReactNode
  className?: string
  arrowsInline?: boolean
  dotsColour?: 'dark' | 'light'
  dotsInside?: boolean
  hideDots?: boolean
  options?: Options
}

// Custom progress bar
const moveProgressBar = (e: SplideType, ref: RefObject<HTMLDivElement>) => {
  const end = e.Components.Controller.getEnd() + 1
  const rate = Math.min((e.Components.Controller.getIndex() + 1) / end, 1)

  if (ref?.current) {
    ref.current.style.width = String(100 * rate) + '%'
  }
}

// Main export
const Slider = ({
  children,
  className,
  arrowsInline = false,
  dotsColour = 'dark',
  dotsInside,
  hideDots = false,
  options,
}: CarouselProps) => {
  const barRef = useRef<HTMLDivElement>(null)

  // Class for the dots wrapper
  const dotsContainerClass = dotsInside ? 'bottom-5' : ' !-bottom-8'

  // Class for the dots items
  const dotsSingularClass =
    dotsColour === 'dark'
      ? ' !bg-neutral-250 [&.is-active]:!bg-teal-green'
      : dotsColour === 'light'
        ? ' !bg-neutral-150 [&.is-active]:!bg-vibrant-green'
        : ''

  // Classes to pass to splide options for override
  const classOptions = {
    classes: {
      pagination:
        'splide__pagination ' +
        dotsContainerClass +
        (hideDots ? ' !hidden' : ''),
      page:
        'splide__pagination__page transition-colors duration-300' +
        dotsSingularClass,
    },
  }

  return (
    <Splide
      className={className ?? 'h-full w-full'}
      hasTrack={false}
      options={{
        type: 'slide',
        autoplay: false,
        perPage: 1,
        arrows: false,
        focus: 0,
        omitEnd: true,
        mediaQuery: 'min',
        ...options,
        ...classOptions,
      }}
      onMounted={(e: SplideType) => {
        arrowsInline && moveProgressBar(e, barRef)
      }}
      onMove={(e: SplideType) => {
        arrowsInline && moveProgressBar(e, barRef)
      }}
    >
      <SplideTrack>
        {Children.map(children, child => {
          if (isValidElement(child)) {
            return (
              <SplideSlide>
                {cloneElement(child, { ...child.props })}
              </SplideSlide>
            )
          }
          return child
        })}
      </SplideTrack>

      {options?.arrows && (
        <div
          className={clsx('splide__arrows', {
            hidden: Children.count(children) == 1,
            'splide__arrows--inline flex items-center gap-5 pt-5': arrowsInline,
          })}
        >
          {arrowsInline && (
            <>
              <div className="carousel-progress mr-3 h-0.5 w-full bg-neutral-200">
                <div
                  ref={barRef}
                  className="carousel-progress-bar h-1 w-0 -translate-y-[1px] rounded-sm bg-forest-green transition-[width] duration-500"
                />
              </div>
            </>
          )}
          <div
            className={clsx({
              'flex gap-1': arrowsInline,
            })}
          >
            <button
              type="button"
              className={clsx(
                'splide__arrow splide__arrow--prev group flex size-10 items-center justify-center rounded-full transition-all',
                {
                  'border border-teal-green bg-transparent disabled:border-neutral-300':
                    arrowsInline,
                  'absolute left-1 top-[50%] z-10 -translate-y-1/2 bg-white drop-shadow-md disabled:opacity-0 2xl:-translate-x-3/4':
                    !arrowsInline,
                }
              )}
            >
              <ChevronLeftIcon
                className={clsx('transition-color size-5 text-teal-green', {
                  'group-disabled:text-neutral-300': arrowsInline,
                })}
              />
            </button>
            <button
              type="button"
              className={clsx(
                'splide__arrow splide__arrow--next group flex size-10 items-center justify-center rounded-full transition-all',
                {
                  'border border-teal-green bg-transparent disabled:border-neutral-300':
                    arrowsInline,
                  'absolute right-1 top-[50%] z-10 -translate-y-1/2 bg-white drop-shadow-md disabled:opacity-0 2xl:translate-x-3/4':
                    !arrowsInline,
                }
              )}
            >
              <ChevronRightIcon
                className={clsx('transition-color size-5 text-teal-green', {
                  'group-disabled:text-neutral-300': arrowsInline,
                })}
              />
            </button>
          </div>
        </div>
      )}
    </Splide>
  )
}

export default Slider
